import React, { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import QRCode from '@medflex/components/stories/QRCode';
import Logo from '@medflex/components/stories/Logo';
import Link from '@medflex/components/stories/Link';
import { trackEvent } from '@medflex/utils/Tracking';
import * as PropTypes from 'prop-types';
import Button from '@medflex/components/stories/Button';
import Modal from '@medflex/components/stories/Modal';
import { IconCheckmark } from '@medflex/components/stories/Icons';
import useUserProfile from '../../hooks/useUserProfile';
import './permanentQRCodePrintFlyer.css';

const PermanentQRCode = ({ onClose }) => {
  const { userProfile } = useUserProfile();
  const qrCodeRef = useRef();
  const { t } = useTranslation('components', { keyPrefix: 'PermanentLink.qrCode' });
  const permanentLink = `${userProfile.attributes.permanentInvitationPatientLink}?utm_medium=qr_code&utm_source=inapp&utm_campaign=invite_patient`;

  const handlePromptPrint = event => {
    trackEvent(event, {
      zone: 'InvitePatientModalModule',
      elementType: 'form',
      actionText: 'qr code Drucken',
    });
    window.print();
  };

  return (
    <Modal
      infoUrl="https://medflex.de/hilfe/behandler-kontakte/patienten-einladen/"
      onClickCloseButton={onClose}
      showCloseButton
      title={t('title')}
      className="print:absolute print:!top-0 print:!left-0 print:!h-full print:!w-full print:!transform-none print:!max-w-none print:!p-0 print:!m-0 print:!round-none print:!shadow-none"
    >
      <div data-component="PatientPermanentQRCodePrintFlyer" className="flex flex-col">
        {/** This div will be hidden for print, see permanentQRCodePrintFlyer */}
        <div className="flex flex-col mb-6 print:hidden">
          <div className="flex flex-col mb-6">
            <div className="text-slate-500">{t('flyer_modal_info')}</div>
          </div>
          <Button onClick={handlePromptPrint}>{t('print_flyer_link')}</Button>
        </div>
        <div className="flex flex-col p-4 pl-6 border self-center shadow-lg print:border-none print:p-0 print:mt-12 print:shadow-none print:w-[16cm]">
          <Logo size="10" className="self-end print:block hidden print:mb-8" />
          <div className="space-y-7 text-blue-800 text-sm print:text-2xl">
            <h1 className="text-xl font-semibold inline print:text-4xl">
              {t('print_flyer.title')}
              <br />
              <span className="text-base print:text-3xl">{t('print_flyer.sub_title')}</span>
            </h1>
            <div>
              <Trans i18nKey="print_flyer.info_text" t={t} />
            </div>
            <div className="mb-6 flex justify-center items-center space-x-4 self-stretch print:gap-10">
              {Object.keys(t(`print_flyer.benefits`, { returnObjects: true })).map(benefit => (
                <div className="flex space-x-1 items-center print:gap-2" key={benefit}>
                  <IconCheckmark size="5" className="text-blue-500 print:h-10 print:w-10" />
                  <div>{t(`print_flyer.benefits.${benefit}`)}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col items-center space-y-3.5 print:text-3xl">
              <QRCode
                ref={qrCodeRef}
                value={permanentLink}
                dataComponent="QRCode"
                className="w-24 print:w-auto print:my-10"
                width={230}
                margin={0}
                color={{ light: '#0000' }}
              />
              <div className="text-blue-500 text-center font-semibold">
                {t('print_flyer.cta_qr')}
              </div>
            </div>
            <div>
              <Trans i18nKey="print_flyer.instruction" t={t} />
            </div>
            <div className="self-start font-semibold print:mt-12">
              {t('print_flyer.learn_more')}{' '}
              <Link
                href="http://www.medflex.de/patient"
                target="_blank"
                className="text-blue-800 font-semibold"
              >
                www.medflex.de/patient
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

PermanentQRCode.propTypes = {
  onClose: PropTypes.func,
};

PermanentQRCode.defaultProps = {
  onClose: () => {},
};

export default PermanentQRCode;
